/**
 * 本校 OSS 视频浏览器直传（STS + ali-oss）。
 * 未开启直传时仍走服务端 InsertVideo；直传失败不回退服务器。
 * 提交保存时再上传，选文件后仅本地预览。最后更新：2026-05-26
 */
import OSS from 'ali-oss'
import axios from 'axios'
import { BaseApiUrl } from '../api/base'
import { isVideoDirectUploadEnabled, getVideoUploadCredential } from '../api/modules/misc';/**
 * 构造 ali-oss 客户端参数。
 * 虚拟主机域名（bucket.oss-cn-xxx.aliyuncs.com）勿再传 endpoint，否则 SDK 双拼 bucket。
 */
function buildOssClientConfig(bucket, region, endpointHost) {
  const config = {
    region: region || 'oss-cn-shanghai',
    bucket,
    secure: true
  }
  const host = (endpointHost || '').replace(/^https?:\/\//i, '').replace(/\/+$/, '')
  if (!host) return config

  const bucketPrefix = bucket + '.'
  const isAliyunVirtualHost =
    host.toLowerCase().indexOf(bucketPrefix.toLowerCase()) === 0 &&
    host.toLowerCase().indexOf('.oss-') > 0
  if (isAliyunVirtualHost) {
    return config
  }

  if (/^oss-[a-z0-9-]+\.aliyuncs\.com$/i.test(host)) {
    config.endpoint = 'https://' + host
    return config
  }

  config.endpoint = 'https://' + host
  config.cname = true
  return config
}

/**
 * 浏览器直传常见网络/CORS 失败（ali-oss 表现为 status -1、connected: false）。最后更新：2026-05-25
 */
function isOssBrowserNetworkError(err) {
  if (!err) return false
  const msg = String(err.message || err.msg || err.code || '')
  const name = String(err.name || '')
  if (err.status === -1 || err.status === 0) return true
  return (
    name === 'RequestError' ||
    msg.indexOf('connected: false') >= 0 ||
    msg.indexOf('ConnectionTimeout') >= 0 ||
    msg.indexOf('XHR error') >= 0
  )
}

/**
 * 将 ali-oss 异常转为用户可读中文提示。最后更新：2026-05-25
 */
function wrapOssUploadError(err) {
  if (isOssBrowserNetworkError(err)) {
    const pageOrigin =
      typeof window !== 'undefined' && window.location && window.location.origin
        ? window.location.origin
        : '（请在浏览器地址栏查看完整来源，含协议、主机、端口）'
    return new Error(
      '视频直传 OSS 失败：请在 Bucket「跨域设置 CORS」中加入当前页面来源：' +
        pageOrigin +
        '；允许 Methods：PUT, POST, HEAD, GET；Allow-Headers：*；Expose：ETag。' +
        ' 勿填 API 地址（如 :9291），须填打开管理端的页面地址。开发可临时用来源 *。'
    )
  }
  const code = err && (err.code || err.name)
  const status = err && err.status
  const detail = err && (err.message || err.msg)
  return new Error(
    (detail || 'OSS 上传失败') +
      (code ? ' [' + code + ']' : '') +
      (status != null && status !== -1 ? ' HTTP ' + status : '')
  )
}

/**
 * 兼容后端 DefaultContractResolver 的 PascalCase 与 camelCase
 */
function pickResponseField(obj, camelKey) {
  if (!obj) return undefined
  if (obj[camelKey] !== undefined && obj[camelKey] !== null) return obj[camelKey]
  const pascalKey = camelKey.charAt(0).toUpperCase() + camelKey.slice(1)
  return obj[pascalKey]
}

/**
 * 上报上传进度 0–100
 */
function notifyUploadProgress(onProgress, percent) {
  if (typeof onProgress !== 'function') return
  const p = Math.min(100, Math.max(0, Math.round(percent)))
  onProgress(p)
}

/**
 * 智能上传视频：STS + ali-oss 直传，未开启时走 InsertVideo。
 * @param {string|number} schoolId - sch_100 或部门/学校数字 Id
 * @param {File} file
 * @param {string} [foo] - 业务目录，同 UploadFileDto.Foo
 * @param {{ onProgress?: function(number): void }} [options]
 * @returns {Promise<{ url: string, objectKey: string }>}
 */
export async function uploadVideoSmart(schoolId, file, foo, options) {
  const onProgress = options && options.onProgress
  notifyUploadProgress(onProgress, 0)
  if (!schoolId) {
    throw new Error('缺少学校标识，无法上传视频')
  }
  if (!file) {
    throw new Error('请选择视频文件')
  }

  const enabledRes = await isVideoDirectUploadEnabled(schoolId)
  const body = enabledRes.data
  const success = pickResponseField(body, 'success') === true || body && body.success === true
  const enabledFlag = pickResponseField(body && body.response, 'enabled')
  const enabled = success && (enabledFlag === true || enabledFlag === 'true' || enabledFlag === 1)
  if (!enabled) {
    return uploadViaInsertVideo(file, foo, schoolId, onProgress)
  }

  const credRes = await getVideoUploadCredential({
    schoolId: String(schoolId),
  })
  const credBody = credRes.data
  const credSuccess = pickResponseField(credBody, 'success') === true || (credBody && credBody.success === true)
  if (!credBody || !credSuccess) {
    throw new Error((credBody && (credBody.msg || credBody.Msg)) || '获取视频上传凭证失败')
  }
  const cred = (credBody && credBody.response) || {}
  const accessKeyId = pickResponseField(cred, 'accessKeyId')
  const accessKeySecret = pickResponseField(cred, 'accessKeySecret')
  const securityToken = pickResponseField(cred, 'securityToken')
  const bucket = pickResponseField(cred, 'bucket')
  const region = pickResponseField(cred, 'region')
  const endpoint = pickResponseField(cred, 'endpoint')
  const objectKey = pickResponseField(cred, 'objectKey')
  const publicUrl = pickResponseField(cred, 'publicUrl')

  if (!accessKeyId || !accessKeySecret || !securityToken || !bucket || !objectKey) {
    throw new Error('上传凭证字段不完整')
  }

  const ossOpts = buildOssClientConfig(bucket, region, endpoint)
  const client = new OSS(Object.assign({}, ossOpts, {
    accessKeyId,
    accessKeySecret,
    stsToken: securityToken,
    authorizationV4: true,
    useFetch: true,
    timeout: 600000,
    retryMax: 2
  }))

  const mime = (file && file.type) || 'video/mp4'
  const putOptions = { mime: mime, headers: { 'Content-Type': mime } }
  const multipartThreshold = 10 * 1024 * 1024
  const ossProgress = function (p) {
    const ratio = typeof p === 'number' ? p : (p && p.loaded && p.total ? p.loaded / p.total : 0)
    notifyUploadProgress(onProgress, ratio * 100)
  }

  try {
    if (file && file.size > multipartThreshold) {
      await client.multipartUpload(objectKey, file, {
        mime: mime,
        parallel: 4,
        partSize: 1024 * 1024,
        timeout: 600000,
        progress: ossProgress,
      })
    } else {
      await client.put(objectKey, file, Object.assign({}, putOptions, { progress: ossProgress }))
    }
  } catch (err) {
    if (err && (err.status === 403 || err.code === 'AccessDenied')) {
      throw new Error('OSS 拒绝写入：请检查 STS Role 策略是否允许 PutObject 到该路径')
    }
    throw wrapOssUploadError(err)
  }

  notifyUploadProgress(onProgress, 100)
  return { url: publicUrl || `https://${endpoint}/${objectKey}`, objectKey }
}

/**
 * 经服务端 InsertVideo 上传（本校未开启 OSS 直传时使用）。
 */
async function uploadViaInsertVideo(file, foo, schoolId, onProgress) {
  const formData = new FormData()
  formData.append('Files', file)
  if (foo) formData.append('Foo', foo)
  if (schoolId != null && schoolId !== '') {
    formData.append('SchId', String(schoolId))
  }
  const res = await axios.post(`${BaseApiUrl}/images/Upload/Video`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 300000,
    onUploadProgress: function (e) {
      if (e && e.total) {
        notifyUploadProgress(onProgress, (e.loaded / e.total) * 100)
      }
    },
  })
  const data = res.data
  const uploadSuccess = pickResponseField(data, 'success') === true || (data && data.success === true)
  if (!data || !uploadSuccess) {
    throw new Error((data && (data.msg || data.Msg)) || '视频上传失败')
  }
  let url = pickResponseField(data, 'response')
  if (url == null) url = data.response
  notifyUploadProgress(onProgress, 100)
  return { url, objectKey: url }
}

/**
 * 选择视频后仅本地预览，不立即上传 OSS/服务器
 */
export function attachLocalVideoFile(file) {
  return {
    pendingVideoFile: file,
    pendingVideoPreviewUrl: URL.createObjectURL(file),
  }
}

/**
 * 释放 blob 预览地址，避免内存泄漏。最后更新：2026-05-25
 */
export function revokeLocalVideoPreview(previewUrl) {
  if (previewUrl && String(previewUrl).startsWith('blob:')) {
    URL.revokeObjectURL(previewUrl)
  }
}

/**
 * 提交保存时再上传；无待传文件则返回已有地址。
 * @param {string|number} schoolId
 * @param {File|null} pendingFile - 用户新选未提交的文件
 * @param {string} existingUrl - 已保存的视频地址
 * @param {string} [foo] - 业务目录
 * @param {object} [options] - 可含 onProgress(0-100)
 */
export async function ensureVideoUploaded(schoolId, pendingFile, existingUrl, foo, options) {
  if (pendingFile) {
    const result = await uploadVideoSmart(schoolId, pendingFile, foo, options)
    return result.url
  }
  return (existingUrl || '').trim()
}

/**
 * 开始显示上传进度（提交保存且有待传视频时调用）。
 */
export function beginVideoUploadProgress(vm) {
  if (!vm) return
  vm.videoUploading = true
  vm.videoUploadProgress = 0
}

/**
 * 结束上传进度显示。
 */
export function endVideoUploadProgress(vm) {
  if (!vm) return
  vm.videoUploading = false
  vm.videoUploadProgress = 0
}

/**
 * 绑定 Vue 实例的上传进度回调，供 ensureVideoUploaded 的 options 使用。
 */
export function createVideoUploadProgressHandlers(vm) {
  return {
    onProgress: function (percent) {
      if (!vm) return
      vm.videoUploading = true
      vm.videoUploadProgress = percent
    },
  }
}
